<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui';
import { MonitorIcon, MoonIcon, SunIcon } from 'lucide-vue-next';
import { useThemeSettings } from '@/composables/useThemeSettings';

const { colorMode, setColorMode } = useThemeSettings();
const { t } = useI18n();

type ColorModePreference = 'light' | 'dark' | 'system';

const colorModeOptions: Array<{
  value: ColorModePreference;
  labelKey: string;
  icon: string;
}> = [
  { value: 'light', labelKey: 'settings.colorMode.light', icon: 'i-lucide-sun' },
  { value: 'dark', labelKey: 'settings.colorMode.dark', icon: 'i-lucide-moon' },
  { value: 'system', labelKey: 'settings.colorMode.system', icon: 'i-lucide-monitor' }
];

const colorModeItems = computed<DropdownMenuItem[][]>(() => [
  colorModeOptions.map((option) => ({
    label: t(option.labelKey),
    icon: option.icon,
    type: 'checkbox' as const,
    checked: colorMode.preference === option.value,
    onUpdateChecked: (checked: boolean) => {
      if (checked) setColorMode(option.value);
    }
  }))
]);

const currentColorModeIcon = computed(() => {
  if (colorMode.preference === 'light') return SunIcon;
  if (colorMode.preference === 'dark') return MoonIcon;
  return MonitorIcon;
});
</script>
<template>
  <UDropdownMenu :items="colorModeItems" :content="{ align: 'end' }" :modal="false">
    <button
      type="button"
      class="p-1 rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/50"
      :title="t('settings.colorMode.title')"
      :aria-label="t('settings.colorMode.title')"
    >
      <ClientOnly>
        <component :is="currentColorModeIcon" class="h-5 w-5" />
      </ClientOnly>
    </button>
  </UDropdownMenu>
</template>
